import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MapPin, Save } from "lucide-react";

const PROVIDER_URLS = {
  osm: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  mapbox: "https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token={token}",
  maptiler: "https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key={token}",
};

// Same resolution as the backend, for the live preview.
function resolveUrl(f) {
  let url = f.tile_provider === "custom" ? (f.url_template || PROVIDER_URLS.osm) : (PROVIDER_URLS[f.tile_provider] || PROVIDER_URLS.osm);
  return url.replace("{token}", f.tile_token || "");
}
function sampleTile(f) {
  return resolveUrl(f).replace("{s}", "a").replace("{z}", "3").replace("{x}", "4").replace("{y}", "3");
}

const blank = () => ({
  tile_provider: "osm", tile_token: "", url_template: "", attribution: "",
  ais_provider: "", ais_api_key: "",
});

export default function MapsSettings() {
  const [f, setF] = useState(blank());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/v1/admin/settings/maps")
      .then(({ data }) => {
        const d = data.data || {};
        setF({
          tile_provider: d.tiles?.provider || "osm",
          tile_token: d.tiles?.tile_token || "",
          url_template: d.tiles?.url_template || "",
          attribution: d.tiles?.attribution || "",
          ais_provider: d.ais?.provider || "",
          ais_api_key: d.ais?.api_key || "",   // "••••••" if already set
        });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load map settings"))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { data: {
        tiles: { provider: f.tile_provider, tile_token: f.tile_token, url_template: f.url_template, attribution: f.attribution },
        ais: { provider: f.ais_provider, api_key: f.ais_api_key },
      }};
      await api.put("/v1/admin/settings/maps", payload);
      toast.success("Map settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Settings</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><MapPin size={24} /> Map provider</h1>
      <p className="text-sm text-[#475569] mt-1">Choose the tile provider for tracking maps. Tile tokens are public (used in the browser); the AIS key is stored encrypted.</p>

      {!loaded ? (
        <div className="vtx-card mt-6 p-6"><div className="vtx-skeleton h-40" /></div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 vtx-card p-6 space-y-4">
            <div className="vtx-eyebrow">Tile layer</div>
            <div className="grid md:grid-cols-2 gap-3">
              <label><span className="vtx-eyebrow block mb-1.5">Provider</span>
                <select className="vtx-input" value={f.tile_provider} onChange={set("tile_provider")} data-testid="map-provider">
                  <option value="osm">OpenStreetMap (no key)</option>
                  <option value="mapbox">Mapbox</option>
                  <option value="maptiler">MapTiler</option>
                  <option value="custom">Custom URL template</option>
                </select>
              </label>
              {f.tile_provider !== "osm" && f.tile_provider !== "custom" && (
                <label><span className="vtx-eyebrow block mb-1.5">Tile token (public)</span><input className="vtx-input font-mono" value={f.tile_token} onChange={set("tile_token")} placeholder="pk.… / MapTiler key" data-testid="map-token" /></label>
              )}
              {f.tile_provider === "custom" && (
                <>
                  <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">URL template</span><input className="vtx-input font-mono text-xs" value={f.url_template} onChange={set("url_template")} placeholder="https://…/{z}/{x}/{y}.png?token={token}" data-testid="map-url" /></label>
                  <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">Tile token (public)</span><input className="vtx-input font-mono" value={f.tile_token} onChange={set("tile_token")} data-testid="map-token" /></label>
                </>
              )}
              <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">Attribution</span><input className="vtx-input" value={f.attribution} onChange={set("attribution")} placeholder="© OpenStreetMap" /></label>
            </div>

            <div className="border-t border-[#A9C0E0]/40 pt-4">
              <div className="vtx-eyebrow">Live vessel positions (AIS) · optional</div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <label><span className="vtx-eyebrow block mb-1.5">Provider</span>
                  <select className="vtx-input" value={f.ais_provider} onChange={set("ais_provider")} data-testid="ais-provider">
                    <option value="">Off</option>
                    <option value="marinetraffic">MarineTraffic</option>
                    <option value="aishub">AISHub</option>
                  </select>
                </label>
                <label><span className="vtx-eyebrow block mb-1.5">API key (encrypted)</span><input className="vtx-input font-mono" value={f.ais_api_key} onChange={set("ais_api_key")} placeholder="leave blank to keep" data-testid="ais-key" /></label>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save} data-testid="save-maps"><Save size={14} /> Save map settings</button>
            </div>
          </div>

          <div className="vtx-card p-5">
            <div className="vtx-eyebrow">Preview</div>
            <div className="mt-3 border border-[#A9C0E0]/60 overflow-hidden" style={{ borderRadius: 2, width: 256, height: 256, maxWidth: "100%" }}>
              <img src={sampleTile(f)} alt="tile preview" width={256} height={256} data-testid="map-preview"
                   onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
            </div>
            <div className="mt-2 text-[10px] text-[#475569] break-all font-mono">{resolveUrl(f)}</div>
            {f.tile_provider !== "osm" && !f.tile_token && (
              <div className="mt-2 text-xs text-[#B45309] font-semibold">Enter a tile token — the preview will stay blank without one.</div>
            )}
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
